import { Avatar, Dropdown, Flex, MenuProps, Typography } from "antd";
import { FC, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router";

import { logout, useAuth } from "@/app/AuthProvider";
import { DEFAULT_CUSTOMER_ROUTE, DEFAULT_SETTINGS_ROUTE } from "@/app/Routes";

import ArrowsUpDownIcon from "@/assets/arrows-up-down.svg?react";
import LogoutIcon from "@/assets/logout.svg?react";
//import NotificationIcon from "@/assets/notification.svg?react";
import ImageIcon from "@/assets/image.svg?react";
import SettingsIcon from "@/assets/settings.svg?react";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useBreadcrumbs } from "@/components/Breadcrumbs/BreadcrumbsContext";
import { getRouteTitle, TitleMap } from "@/utils/getRouteTitle";
import { useTranslation } from "react-i18next";

//const MOCK_NOTIFICATION_COUNT = 23;

export const Header: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const breadcrumbs = useBreadcrumbs();
  const { t, i18n } = useTranslation();

  const titleMap: TitleMap = useMemo(
    () => ({
      courses: t("header.titles.courses"),
      dashboard: t("header.titles.dashboard"),
      programs: t("header.titles.programs"),
      users: t("header.titles.users"),
      posts: t("header.titles.posts"),
      notifications: t("header.titles.notifications"),
      "protected-terms": t("header.titles.protected-terms"),
      "activity-log": t("header.titles.activity-log"),
      reports: {
        title: t("header.titles.reports"),
        subRoutes: {
          programs: t("header.titles.program-reports"),
          courses: t("header.titles.course-reports"),
          users: t("header.titles.user-reports"),
        },
      },
    }),
    [t]
  );

  const title = useMemo(() => getRouteTitle(pathname, titleMap), [pathname, titleMap]);
  const { avatar } = useAuth();

  const isFRLanguage = i18n.language !== "en";

  const profileItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "settings",
        label: (
          <Flex align="center" justify="space-between">
            <span>{t("user.menu.settings")}</span>
            <SettingsIcon />
          </Flex>
        ),
        onClick: () => navigate(DEFAULT_SETTINGS_ROUTE),
      },
      {
        key: "switchToLearning",
        label: (
          <Flex align="center" justify="space-between">
            <span>{t("user.menu.switch-lern")}</span>
            <ArrowsUpDownIcon />
          </Flex>
        ),
        onClick: () => navigate(DEFAULT_CUSTOMER_ROUTE),
      },
      {
        key: "logout",
        label: (
          <Flex align="center" justify="space-between">
            <span>{t("user.menu.logout")}</span>
            <LogoutIcon />
          </Flex>
        ),
        onClick: logout,
      },
    ],
    [t, navigate]
  );

  return (
    <Flex rootClassName="header-container" justify="space-between" align="center">
      <Helmet>
        <title>{title ? `CONNECT | ${title}` : `CONNECT`}</title>
        {title && <meta name="description" content={`${title} page`} />}
      </Helmet>
      {!!breadcrumbs.length && <Breadcrumbs breadcrumbs={breadcrumbs} />}
      {title && (
        <Typography.Title level={3} className="header-title">
          {title}
        </Typography.Title>
      )}
      <Flex justify="right" align="center" gap={28}>
        {/*         <Badge count={MOCK_NOTIFICATION_COUNT > 9 ? "9+" : MOCK_NOTIFICATION_COUNT} color="blue">
          <Button className="notification-btn" type="text" icon={<NotificationIcon />} />
        </Badge> */}
        <Dropdown menu={{ items: profileItems }} placement="bottomRight" overlayStyle={{ width: isFRLanguage ? 260 : 200 }}>
          {avatar ? <Avatar className="profile-avatar" src={avatar} /> : <Avatar className="profile-avatar" icon={<ImageIcon />} />}
        </Dropdown>
      </Flex>
    </Flex>
  );
};
